import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatCurrency, formatDate } from '@/lib/utils'
import { ArrowLeft, Send, CheckCircle2, FileText, ArrowRight, Wrench, AlertCircle, XCircle } from 'lucide-react'
import Combobox from '@/components/Combobox'
import { toast } from 'sonner'
import { notifyPortal } from '@/lib/portalNotify'
import { motion } from 'framer-motion'
import LineItemEditor, { type LineItem } from '@/components/payments/LineItemEditor'
import TotalsPanel from '@/components/payments/TotalsPanel'
import { getQuoteStatusConfig } from './quote-status'
import { computeTotals, type DiscountType } from '@/lib/totals'

export default function QuoteDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { workspace, user } = useAuth()
  const queryClient = useQueryClient()
  const [lineItems, setLineItems] = useState<LineItem[]>([])
  const [taxRate, setTaxRate] = useState(0)
  const [discountType, setDiscountType] = useState<DiscountType>('none')
  const [discountValue, setDiscountValue] = useState(0)
  // Arc B Slice 1: quote-level minimum charge (null = none). Floors the subtotal.
  const [minimumCharge, setMinimumCharge] = useState<number | null>(null)
  // Sales Slice 1 CP2: the deal this quote is attached to (null = unlinked).
  const [dealId, setDealId] = useState<string | null>(null)
  const [dealPickerQuery, setDealPickerQuery] = useState('')
  const [changingDeal, setChangingDeal] = useState(false)
  const [notes, setNotes] = useState('')
  const [title, setTitle] = useState('')
  const [serviceLineId, setServiceLineId] = useState('')
  const [saving, setSaving] = useState(false)
  const [converting, setConverting] = useState(false)

  // Active service lines for the picker. Workspace-scoped via RLS,
  // ordered by the operator-controlled sort_order from 20260607020000.
  const { data: serviceLines = [] } = useQuery({
    queryKey: ['quote-service-lines', workspace?.id],
    enabled: !!workspace?.id,
    queryFn: async () => {
      const { data } = await supabase.from('service_lines')
        .select('id, name')
        .eq('workspace_id', workspace!.id)
        .eq('is_active', true)
        .order('sort_order')
      return data ?? []
    },
  })

  const { data: quote, isLoading } = useQuery({
    queryKey: ['quote', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('quotes').select('*').eq('id', id).single()
      if (error) throw error
      return data
    },
    enabled: !!id,
  })

  useEffect(() => {
    if (quote) {
      setLineItems(quote.line_items || [])
      setTaxRate(quote.tax_rate || 0)
      // Cast: discount_type is `string` in generated types because CHECK
      // constraints don't narrow Postgres types. The DB-level CHECK guarantees
      // the value is one of the union members.
      setDiscountType((quote.discount_type as DiscountType) || 'none')
      setDiscountValue(quote.discount_value || 0)
      setMinimumCharge((quote as any)?.minimum_charge ?? null)
      setDealId((quote as any)?.deal_id ?? null)
      setNotes(quote.notes || '')
      setTitle(quote.title || '')
      setServiceLineId((quote as { service_line_id?: string | null }).service_line_id || '')
    }
  }, [quote?.id])

  const totals = useCallback(() => {
    // Arc B Slice 1: floor the subtotal by the quote's minimum_charge (null =
    // none). Cast — minimum_charge isn't in the generated types yet.
    const { subtotal, discountAmount, taxAmount, total } = computeTotals({
      lineItems, discountType, discountValue, taxRate,
      minimumCharge,
    })
    return { subtotal, discountAmount, taxAmount, total }
  }, [lineItems, discountType, discountValue, taxRate, minimumCharge])

  const updateMutation = useMutation({
    mutationFn: async (updates: Record<string, any>) => {
      const { error } = await supabase.from('quotes').update(updates).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', id] })
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
    },
    onError: () => toast.error('Failed to save'),
  })

  const saveQuote = async () => {
    setSaving(true)
    const { subtotal, discountAmount, taxAmount, total } = totals()
    await updateMutation.mutateAsync({
      line_items: lineItems, subtotal,
      discount_type: discountType, discount_value: discountValue, discount_amount: discountAmount, minimum_charge: minimumCharge,
      tax_rate: taxRate, tax_amount: taxAmount, total, notes, title,
      service_line_id: serviceLineId || null,
    })
    setSaving(false)
    toast.success('Quote saved')
  }

  // Orphan-detection lookups. When a convert handler hits a partial-failure
  // (UPDATE quote.status flipped, downstream INSERT failed), we end up with
  // a quote in 'invoiced'/'won' state but no linked downstream record. The
  // queries below probe for the downstream row by quote_id; if absent, the
  // operator gets a "Conversion incomplete — retry" affordance instead of
  // being stuck.
  const { data: linkedInvoice } = useQuery({
    queryKey: ['quote-linked-invoice', id, quote?.status],
    queryFn: async () => {
      const { data } = await supabase.from('invoices').select('id').eq('quote_id', id).maybeSingle()
      return data
    },
    enabled: !!id && quote?.status === 'invoiced',
  })

  const { data: linkedJob } = useQuery({
    queryKey: ['quote-linked-job', id, quote?.status],
    queryFn: async () => {
      const { data } = await supabase.from('field_jobs').select('id').eq('quote_id', id).maybeSingle()
      return data
    },
    enabled: !!id && quote?.status === 'won',
  })

  // Sales Slice 1 CP2: the linked deal's display fields (mirror linkedInvoice/
  // linkedJob). Cast — deal_id isn't in the generated types yet.
  const { data: linkedDeal } = useQuery({
    queryKey: ['quote-linked-deal', dealId],
    queryFn: async () => {
      const { data } = await (supabase as any).from('deals').select('id,title,value,stage_name,status').eq('id', dealId).maybeSingle()
      return data
    },
    enabled: !!dealId,
  })

  // Candidate deals for the "Link a deal" picker — all workspace deals (a quote
  // can attach to any deal; the row shows stage/status to disambiguate).
  const { data: allDeals = [] } = useQuery({
    queryKey: ['quote-deal-candidates', workspace?.id],
    queryFn: async () => {
      const { data } = await (supabase as any).from('deals').select('id,title,value,stage_name,status').eq('workspace_id', workspace!.id).order('created_at', { ascending: false })
      return data || []
    },
    enabled: !!workspace?.id,
  })

  // Immediate-write persistence (Option B — mirrors CP1's deal-side attach/detach;
  // deal_id is the quote's own field, written directly, NOT threaded through the
  // save objects, which include invoice/job INSERTs that must not carry deal_id).
  const linkDeal = useMutation({
    mutationFn: async (newDealId: string) => {
      const { error } = await (supabase as any).from('quotes').update({ deal_id: newDealId }).eq('id', id)
      if (error) throw error
      return newDealId
    },
    onSuccess: (newDealId) => {
      setDealId(newDealId as string)
      setChangingDeal(false)
      queryClient.invalidateQueries({ queryKey: ['quote-linked-deal'] })
      toast.success('Deal linked')
    },
    onError: () => toast.error('Failed to link deal'),
  })
  const unlinkDeal = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase as any).from('quotes').update({ deal_id: null }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      setDealId(null)
      setChangingDeal(false)
      queryClient.invalidateQueries({ queryKey: ['quote-linked-deal'] })
      toast.success('Deal unlinked')
    },
    onError: () => toast.error('Failed to unlink deal'),
  })

  // Convert quote → invoice. Two-step (INSERT invoice → UPDATE quote.status)
  // ordered so the downstream record exists BEFORE the quote flips to
  // 'invoiced'. If INSERT fails, quote stays in 'approved' and operator can
  // retry. If UPDATE fails after INSERT succeeds, the new invoice exists with
  // quote_id set; orphan-detection (see linkedInvoice query) shows "View
  // existing invoice" instead of the convert button.
  const convertToInvoice = async () => {
    if (!quote) return
    setConverting(true)
    const { subtotal, discountAmount, taxAmount, total } = totals()
    // Save current edits first so the snapshot in the invoice matches what
    // the operator sees on screen — but DON'T flip status yet.
    await supabase.from('quotes').update({
      line_items: lineItems, subtotal,
      discount_type: discountType, discount_value: discountValue, discount_amount: discountAmount, minimum_charge: minimumCharge,
      tax_rate: taxRate, tax_amount: taxAmount, total, notes, title,
    }).eq('id', id)
    // invoice_number assigned by the trigger (Phase A, race-safe). Omit the key.
    const { data: invoice, error: invErr } = await supabase.from('invoices').insert({
      contact_id: quote.contact_id || null,
      contact_name: quote.contact_name,
      contact_email: quote.contact_email || '',
      company_id: quote.company_id || null,
      company_name: quote.company_name || null,
      quote_id: id,
      status: 'draft',
      line_items: lineItems,
      subtotal, tax_rate: taxRate, tax_amount: taxAmount,
      total, amount_due: total, amount_paid: 0,
      discount_type: discountType, discount_value: discountValue, discount_amount: discountAmount, minimum_charge: minimumCharge,
      currency: 'USD',
      issue_date: new Date().toISOString().split('T')[0],
      notes,
      workspace_id: workspace!.id,
      // Attribution inheritance: the quote's service_line_id flows to
      // the invoice (frozen there per invoices.service_line_id comment).
      service_line_id: serviceLineId || null,
    }).select().single()
    if (invErr || !invoice) {
      setConverting(false)
      toast.error(`Error: ${invErr?.message || 'Failed to create invoice'}`)
      return
    }
    // Invoice exists — now flip quote status. If this fails, the invoice is
    // still findable via quote_id; orphan-detection surfaces "View invoice".
    const { error: qErr } = await supabase.from('quotes').update({ status: 'invoiced' }).eq('id', id)
    setConverting(false)
    if (qErr) {
      toast.warning(`Invoice created, but quote status update failed: ${qErr.message}`)
    } else {
      toast.success('Converted to invoice!')
    }
    queryClient.invalidateQueries({ queryKey: ['quotes'] })
    queryClient.invalidateQueries({ queryKey: ['invoices'] })
    navigate(`/app/invoices/${invoice.id}`)
  }

  // Convert quote → field job. Mirrors convertToInvoice's structure exactly.
  // Same INSERT-first ordering so the downstream record exists before the
  // quote flips to 'won'. Auto-populates contact + company snapshots from
  // the quote; description from quote.title; scheduled_date defaulted to
  // quote.valid_until or today.
  const convertToJob = async () => {
    if (!quote) return
    if (!quote.contact_id) {
      toast.error('Quote has no contact — link a contact before converting to job')
      return
    }
    setConverting(true)
    const { subtotal, discountAmount, taxAmount, total } = totals()
    await supabase.from('quotes').update({
      line_items: lineItems, subtotal,
      discount_type: discountType, discount_value: discountValue, discount_amount: discountAmount, minimum_charge: minimumCharge,
      tax_rate: taxRate, tax_amount: taxAmount, total, notes, title,
    }).eq('id', id)
    const { count } = await supabase.from('field_jobs').select('id', { count: 'exact', head: true }).eq('workspace_id', workspace!.id)
    const jobNumber = `JOB-${String((count || 0) + 1).padStart(4, '0')}`
    const today = new Date().toISOString().split('T')[0]
    const { data: job, error: jErr } = await supabase.from('field_jobs').insert({
      job_number: jobNumber,
      title: quote.title || `Job from ${quote.quote_number}`,
      contact_id: quote.contact_id,
      contact_name: quote.contact_name,
      contact_phone: '',
      company_name: quote.company_name || null,
      // company_id stamped from the quote's direct company link (quotes
      // already carry company_id — see invoices/quotes precedent). Same
      // frozen-attribution rule as company_name (lib/contactSnapshot.ts).
      company_id: quote.company_id || null,
      status: 'scheduled',
      priority: 'normal',
      scheduled_date: quote.valid_until || today,
      scheduled_start: '09:00',
      scheduled_end: '10:00',
      checklist_complete: false,
      payment_collected: false,
      job_notes: notes,
      quote_id: id,
      workspace_id: workspace!.id,
      // Attribution inheritance — the chain continues if this job
      // later generates an invoice (FieldHub/JobDetail paths inherit
      // from job, established in 20260607020000).
      service_line_id: serviceLineId || null,
    }).select().single()
    if (jErr || !job) {
      setConverting(false)
      toast.error(`Error: ${jErr?.message || 'Failed to create job'}`)
      return
    }
    const { error: qErr } = await supabase.from('quotes').update({ status: 'won' }).eq('id', id)
    setConverting(false)
    if (qErr) {
      toast.warning(`Job created, but quote status update failed: ${qErr.message}`)
    } else {
      toast.success('Converted to job!')
    }
    // The convert path always sets scheduled_date (quote.valid_until or
    // today), so this is always a NULL→date transition for the new job.
    notifyPortal({ event: 'job_scheduled', job_id: job.id })
    queryClient.invalidateQueries({ queryKey: ['quotes'] })
    queryClient.invalidateQueries({ queryKey: ['field-jobs-cal'] })
    navigate(`/app/field?tab=board&job=${job.id}`)
  }

  if (isLoading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: 'var(--canvas)' }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid var(--accent)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
    </div>
  )

  if (!quote) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-secondary)' }}>Quote not found</div>

  const { subtotal, discountAmount, taxAmount, total } = totals()
  const cfg = getQuoteStatusConfig(quote.status)
  const isLocked = ['invoiced', 'declined', 'won'].includes(quote.status)

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>

      {/* Top bar */}
      <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: 'var(--surface-raised)' }}>
        <button onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/app/quotes') }}
          style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--ink-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--ink-primary)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
          <ArrowLeft style={{ width: 14, height: 14 }} /> Back
        </button>
        <div style={{ width: 1, height: 16, background: 'var(--border-default)' }} />
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', fontFamily: 'monospace' }}>{quote.quote_number}</span>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, textTransform: 'capitalize' }}>
          {cfg.label}
        </span>
        {quote.portal_responded_by && (quote.status === 'approved' || quote.status === 'declined') && (
          <PortalResponseStamp
            decision={quote.status as 'approved' | 'declined'}
            respondedAt={(quote.status === 'approved' ? quote.approved_at : quote.declined_at) as string}
            portalUserId={quote.portal_responded_by as string}
            note={quote.portal_response_note as string | null} />
        )}
        {/* Recommendation chain — visible when a member recommended.
            Persists alongside the approval stamp once an admin completes
            the chain (recommended_at survives the status flip). */}
        {quote.recommended_by && quote.recommended_at && (
          <PortalRecommendStamp
            recommendedAt={quote.recommended_at as string}
            portalUserId={quote.recommended_by as string} />
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {!isLocked && (
            <>
              <button onClick={saveQuote} disabled={saving}
                style={{ padding: '7px 14px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              {quote.status === 'draft' && (
                <button onClick={async () => {
                    const { subtotal, discountAmount, taxAmount, total } = totals()

                    // Step 1: persist the status transition + current line-item
                    // state. FieldHub lesson: never fire the notification before
                    // the row is durable.
                    try {
                      await updateMutation.mutateAsync({
                        status: 'sent',
                        sent_at: new Date().toISOString(),
                        line_items: lineItems,
                        subtotal,
                        discount_type: discountType, discount_value: discountValue, discount_amount: discountAmount, minimum_charge: minimumCharge,
                        tax_rate: taxRate, tax_amount: taxAmount, total,
                        notes, title,
                      })
                    } catch (err) {
                      toast.error('Failed to save quote — not sent')
                      return
                    }

                    // Step 2: invoke quote-sent-notify. The edge function decides
                    // portal-CTA vs full-summary based on whether the contact has
                    // an active portal_users row, and handles the workspace name
                    // / branding / Resend pipeline server-side. Graceful degrades
                    // (warning / skipped) — never blocks the send flow.
                    try {
                      const { data, error } = await supabase.functions.invoke('quote-sent-notify', {
                        body: { quote_id: quote.id },
                      })
                      if (error) {
                        toast.warning(`Quote sent · email failed: ${error.message}`)
                      } else if (data?.skipped) {
                        if (data.reason === 'contact_has_no_email') {
                          toast.warning('Quote sent · no email on file for contact — nothing emailed.')
                        } else {
                          toast.success('Quote sent')
                        }
                      } else if (data?.warning) {
                        toast.warning(`Quote sent · email failed: ${data.warning}`)
                      } else if (data?.sent) {
                        toast.success(
                          data.portal_cta
                            ? `Quote sent · ${data.recipient} (portal review link included)`
                            : `Quote sent · ${data.recipient}`
                        )
                      } else {
                        toast.success('Quote sent')
                      }
                    } catch (err) {
                      toast.warning(`Quote sent · email failed: ${err instanceof Error ? err.message : 'unknown error'}`)
                    }
                  }}
                  className="btn-primary" style={{ padding: '7px 14px', fontSize: 12 }}>
                  <Send style={{ width: 13, height: 13 }} /> Send
                </button>
              )}
              {['sent', 'viewed'].includes(quote.status) && (
                <button onClick={async () => {
                    const { subtotal, discountAmount, taxAmount, total } = totals()
                    await updateMutation.mutateAsync({
                      status: 'approved',
                      approved_at: new Date().toISOString(),
                      line_items: lineItems,
                      subtotal,
                      discount_type: discountType, discount_value: discountValue, discount_amount: discountAmount, minimum_charge: minimumCharge,
                      tax_rate: taxRate,
                      tax_amount: taxAmount,
                      total,
                      notes,
                      title,
                    })
                    toast.success('Quote approved')
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'color-mix(in srgb, var(--positive) var(--status-tint-dim), transparent)', border: '1px solid var(--positive-border)', borderRadius: 8, color: 'var(--positive)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  <CheckCircle2 style={{ width: 13, height: 13 }} /> Approve
                </button>
              )}
              {quote.status === 'approved' && (
                <>
                  <button onClick={convertToJob} disabled={converting}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'color-mix(in srgb, var(--positive) var(--status-tint-dim), transparent)', border: '1px solid var(--positive-border)', borderRadius: 8, color: 'var(--positive)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    <Wrench style={{ width: 13, height: 13 }} />
                    Convert to job
                  </button>
                  <button onClick={convertToInvoice} disabled={converting}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--logo-bg-gradient)', border: 'none', borderRadius: 8, color: 'var(--accent-ink)', fontSize: 12, fontWeight: 700, cursor: 'pointer', boxShadow: 'var(--shadow-glow-accent)' }}>
                    <FileText style={{ width: 13, height: 13 }} />
                    {converting ? 'Converting...' : 'Convert to invoice'}
                    <ArrowRight style={{ width: 13, height: 13 }} />
                  </button>
                </>
              )}
            </>
          )}
          {quote.status === 'invoiced' && linkedInvoice && (
            <button onClick={() => navigate(`/app/invoices/${linkedInvoice.id}`)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'color-mix(in srgb, var(--warning) var(--status-tint-faint), transparent)', border: '1px solid var(--warning-border)', borderRadius: 8, color: 'var(--warning)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              <FileText style={{ width: 13, height: 13 }} /> View invoice
            </button>
          )}
          {quote.status === 'invoiced' && linkedInvoice === null && (
            <button onClick={convertToInvoice} disabled={converting}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'color-mix(in srgb, var(--danger) var(--status-tint-dim), transparent)', border: '1px solid var(--danger-border)', borderRadius: 8, color: 'var(--danger)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              <AlertCircle style={{ width: 13, height: 13 }} />
              {converting ? 'Retrying...' : 'Conversion incomplete — retry'}
            </button>
          )}
          {quote.status === 'won' && linkedJob && (
            <button onClick={() => navigate(`/app/field?tab=board&job=${linkedJob.id}`)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'color-mix(in srgb, var(--positive) var(--status-tint-faint), transparent)', border: '1px solid var(--positive-border)', borderRadius: 8, color: 'var(--positive)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              <Wrench style={{ width: 13, height: 13 }} /> View job
            </button>
          )}
          {quote.status === 'won' && linkedJob === null && (
            <button onClick={convertToJob} disabled={converting}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'color-mix(in srgb, var(--danger) var(--status-tint-dim), transparent)', border: '1px solid var(--danger-border)', borderRadius: 8, color: 'var(--danger)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              <AlertCircle style={{ width: 13, height: 13 }} />
              {converting ? 'Retrying...' : 'Conversion incomplete — retry'}
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '28px 32px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32 }}>
            <div>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14, boxShadow: 'var(--logo-glow)' }}>
                <span style={{ fontSize: 18, fontWeight: 900, color: 'var(--accent-ink)' }}>O</span>
              </div>
              <p style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', margin: '0 0 4px', letterSpacing: '-0.02em' }}>Orkestra</p>
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>{workspace?.name}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: 28, fontWeight: 900, color: 'var(--accent-text)', margin: '0 0 6px', letterSpacing: '-0.03em', fontFamily: 'monospace' }}>{quote.quote_number}</p>
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 4px' }}>Issued {formatDate(quote.issue_date)}</p>
              {quote.valid_until && (
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Valid until {formatDate(quote.valid_until)}</p>
              )}
            </div>
          </div>

          {/* Title */}
          {!isLocked ? (
            <div style={{ marginBottom: 24 }}>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Quote title..."
                style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink-primary)', background: 'transparent', border: 'none', outline: 'none', width: '100%', padding: '4px 0', borderBottom: '1px solid var(--border-subtle)' }}
                onFocus={e => e.currentTarget.style.borderBottomColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderBottomColor = 'var(--border-subtle)'} />
              {/* Service-line picker: optional, just below the title so it
                  reads as a quote-level attribute next to the document
                  identifier. Active rows only, sort_order ordering. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Service line</label>
                <select value={serviceLineId} onChange={e => setServiceLineId(e.target.value)}
                  style={{ padding: '6px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
                  <option value="">—</option>
                  {serviceLines.map(sl => <option key={sl.id} value={sl.id}>{sl.name}</option>)}
                </select>
              </div>
            </div>
          ) : quote.title ? (
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 24px' }}>{quote.title}</h2>
          ) : null}

          {/* Bill to */}
          <div style={{ marginBottom: 32, padding: '16px 20px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12 }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Prepared for</p>
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 4px' }}>{quote.contact_name}</p>
            {quote.company_name && <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>{quote.company_name}</p>}
            {quote.contact_email && <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>{quote.contact_email}</p>}

            {/* Sales Slice 1 CP2: linked deal — display + picker (immediate write). */}
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Deal</p>
              {dealId && !changingDeal ? (
                linkedDeal ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <button type="button" onClick={() => navigate(`/app/deals/${(linkedDeal as any).id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(linkedDeal as any).title}</span>
                      <span style={{ fontSize: 12, color: 'var(--positive)', whiteSpace: 'nowrap' }}>{formatCurrency((linkedDeal as any).value || 0)}</span>
                      <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', whiteSpace: 'nowrap' }}>{(linkedDeal as any).stage_name}</span>
                    </button>
                    {!isLocked && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <button type="button" onClick={() => setChangingDeal(true)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', fontSize: 11, fontWeight: 600, padding: 0 }}>Change</button>
                        <button type="button" aria-label="Unlink deal" onClick={() => unlinkDeal.mutate()}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0, display: 'flex', alignItems: 'center' }}
                          onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger)' }}
                          onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-tertiary)' }}>
                          <XCircle style={{ width: 14, height: 14 }} />
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Loading…</p>
                )
              ) : isLocked ? (
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No deal linked</p>
              ) : (
                <Combobox
                  value={null}
                  inputValue={dealPickerQuery}
                  onInputChange={setDealPickerQuery}
                  onChange={(d: any) => { if (d) { linkDeal.mutate(d.id); setDealPickerQuery('') } }}
                  options={allDeals as any[]}
                  getKey={(d: any) => d.id}
                  getLabel={(d: any) => d.title}
                  renderOption={(d: any) => <span style={{ fontSize: 12 }}>{d.title} · {formatCurrency(d.value || 0)} · {d.stage_name ?? '—'} · {d.status}</span>}
                  placeholder="Link a deal…"
                  emptyMessage="No deals"
                />
              )}
            </div>
          </div>

          {/* Line items */}
          <LineItemEditor
            items={lineItems}
            onItemsChange={setLineItems}
            isLocked={isLocked}
            workspaceId={workspace!.id}
          />

          {/* Totals */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginBottom: 24 }}>
            <TotalsPanel
              subtotal={subtotal}
              discountType={discountType}
              discountValue={discountValue}
              discountAmount={discountAmount}
              onDiscountTypeChange={setDiscountType}
              onDiscountValueChange={setDiscountValue}
              taxRate={taxRate}
              taxAmount={taxAmount}
              onTaxRateChange={setTaxRate}
              total={total}
              isLocked={isLocked}
            />
            {/* Arc B Slice 1: minimum-charge input below TotalsPanel (shared
                component untouched). Floors the subtotal; totals reflect it live. */}
            <div style={{ width: 280, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
              <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>Minimum charge</span>
              {isLocked ? (
                <span style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>
                  {minimumCharge != null ? formatCurrency(minimumCharge) : '—'}
                </span>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>$</span>
                  <input type="number" min={0} step={0.01}
                    value={minimumCharge ?? ''}
                    onChange={e => { const v = parseFloat(e.target.value); setMinimumCharge(Number.isFinite(v) && v > 0 ? v : null) }}
                    placeholder="None"
                    style={{ width: 72, padding: '2px 6px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 5, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', textAlign: 'right' }} />
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {!isLocked ? (
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Scope of work, terms, next steps..." rows={3}
                style={{ width: '100%', padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, fontSize: 13, color: 'var(--ink-secondary)', outline: 'none', resize: 'vertical', lineHeight: 1.5, boxSizing: 'border-box' }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'} />
            </div>
          ) : quote.notes ? (
            <div style={{ padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10 }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>Notes</p>
              <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.6 }}>{quote.notes}</p>
            </div>
          ) : null}

          {/* Convert CTA */}
          {quote.status === 'approved' && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              style={{ marginTop: 24, padding: '20px 24px', background: 'linear-gradient(135deg, color-mix(in srgb, var(--accent) var(--status-tint-dim), transparent), color-mix(in srgb, var(--positive) var(--status-tint-faint), transparent))', border: '1px solid var(--accent-border)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div>
                <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 4px' }}>Quote approved — convert to invoice or job</p>
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Invoice for billing; field job for scheduling and dispatch. All line items / details copy over.</p>
              </div>
              <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
                <button onClick={convertToJob} disabled={converting}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 18px', background: 'color-mix(in srgb, var(--positive) var(--status-tint-dim), transparent)', border: '1px solid var(--positive-border)', borderRadius: 10, color: 'var(--positive)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  <Wrench style={{ width: 14, height: 14 }} />
                  Convert to job
                </button>
                <button onClick={convertToInvoice} disabled={converting}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 20px', background: 'var(--logo-bg-gradient)', border: 'none', borderRadius: 10, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: 'var(--shadow-glow-accent)' }}>
                  <FileText style={{ width: 14, height: 14 }} />
                  {converting ? 'Converting...' : 'Convert to invoice'}
                  <ArrowRight style={{ width: 14, height: 14 }} />
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

    </div>
  )
}

// Staff-side stamp shown next to the status pill when a portal user
// responded. Looks up the responder name via portal_users → contacts;
// uses RLS workspace_isolation so the lookup is naturally scoped.
function PortalResponseStamp({ decision, respondedAt, portalUserId, note }: {
  decision: 'approved' | 'declined'
  respondedAt: string
  portalUserId: string
  note: string | null
}) {
  const { data: responder } = useQuery({
    queryKey: ['portal-responder', portalUserId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('portal_users')
        .select('id, contact:contacts(first_name, last_name, email)')
        .eq('id', portalUserId)
        .maybeSingle()
      if (error) throw error
      const c = (data as any)?.contact
      const name = [c?.first_name, c?.last_name].filter(Boolean).join(' ').trim() || null
      return { name, email: c?.email ?? null }
    },
  })
  const isApprove = decision === 'approved'
  const stamp = (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700,
      padding: '3px 9px', borderRadius: 100,
      background: isApprove ? 'var(--positive-dim)' : 'var(--danger-dim)',
      color: isApprove ? 'var(--positive)' : 'var(--danger)',
      border: `1px solid ${isApprove ? 'var(--positive-border)' : 'var(--danger-border)'}`,
    }}>
      {isApprove ? <CheckCircle2 style={{ width: 11, height: 11 }} /> : <XCircle style={{ width: 11, height: 11 }} />}
      via portal{responder?.name ? ` · ${responder.name}` : ''} · {formatDate(respondedAt, 'MMM d, yyyy')}
    </span>
  )
  // If there's a note, wrap with a title attribute so hovering reveals it
  // without consuming layout space in the dense header row.
  return note ? <span title={note}>{stamp}</span> : stamp
}

// Companion stamp showing the recommendation half of the audit chain.
// When a member recommends, this stamp appears alongside the response
// stamp once the admin completes the chain (and on its own while the
// quote is recommendation-pending in staff QuoteDetail loads).
function PortalRecommendStamp({ recommendedAt, portalUserId }: {
  recommendedAt: string
  portalUserId: string
}) {
  const { data: responder } = useQuery({
    queryKey: ['portal-recommender', portalUserId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('portal_users')
        .select('id, contact:contacts(first_name, last_name, email)')
        .eq('id', portalUserId)
        .maybeSingle()
      if (error) throw error
      const c = (data as any)?.contact
      const name = [c?.first_name, c?.last_name].filter(Boolean).join(' ').trim() || null
      return { name, email: c?.email ?? null }
    },
  })
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700,
      padding: '3px 9px', borderRadius: 100,
      background: 'var(--info-dim)',
      color: 'var(--info)',
      border: '1px solid var(--info-border)',
    }}>
      Recommended{responder?.name ? ` by ${responder.name}` : ''} · {formatDate(recommendedAt, 'MMM d, yyyy')}
    </span>
  )
}